import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import privaxiaLogo from '@/assets/privaxia-logo.png';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginLocalPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orgSlug = searchParams.get('org');
  const [branding, setBranding] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!orgSlug) return;
    axios
      .get(`${BACKEND_URL}/api/branding/public/${orgSlug}`)
      .then(({ data }) => setBranding(data))
      .catch(() => setBranding(null));
  }, [orgSlug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        formData,
        { withCredentials: true }
      );
      
      console.log('Login response:', response.data);
      
      if (response.data.success) {
        const { user } = response.data;
        
        toast.success('Login efetuado com sucesso!');
        
        // Pequeno delay para garantir que o cookie foi setado
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const dashboardPath = user.is_dpo_global
          ? '/dpo/tenants'
          : user.role === 'admin' ? '/admin' : user.role === 'operador' ? '/operador' : user.role === 'dpo' ? '/dpo' : '/dashboard';
        navigate(dashboardPath, { state: { user }, replace: true });
      } else {
        toast.error('Erro no login');
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Credenciais inválidas';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1765366417030-16d9765d920a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBkYXRhJTIwc2VjdXJpdHl8ZW58MHx8fHwxNzY4OTk3NjY2fDA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A]/90 to-[#0F172A]/70" />
        <div className="relative z-10 flex flex-col justify-center items-start p-12 text-white">
          <img src={privaxiaLogo} alt="Privaxia Logo" className="w-20 h-20 object-contain mb-6" />
          <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Privaxia
          </h1>
          <p className="text-xl text-slate-300 max-w-md">
            Data Privacy, GDPR Consent & Compliance
          </p>
        </div>
      </div>
      
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md space-y-8">
          {branding?.login_banner_url && (
            <img
              src={`${BACKEND_URL}${branding.login_banner_url}`}
              alt={branding.name}
              data-testid="tenant-login-banner"
              className="w-full rounded-lg border border-slate-200 shadow-sm object-cover"
              style={{ aspectRatio: '1820 / 400' }}
            />
          )}
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <img src={privaxiaLogo} alt="Privaxia Logo" className="w-16 h-16 object-contain" />
            </div>
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {branding?.name || 'Privaxia'}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Login com Email e Password
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-sm border border-slate-200 space-y-6">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="local-login-email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                className="mt-2"
                placeholder="seu@email.com"
              />
            </div>
            
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="local-login-password-input"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                className="mt-2"
                placeholder="••••••••"
              />
            </div>

            <Button
              data-testid="local-login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#0F172A] hover:bg-[#0F172A]/90 text-white font-medium"
            >
              {loading ? 'A entrar...' : 'Entrar'}
            </Button>
            
            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-slate-500 hover:text-[#10B981] hover:underline"
              >
                Esqueceu a password?
              </button>
            </div>
            
            <div className="text-center text-sm space-y-2">
              <div>
                <span className="text-slate-600">Não tem conta? </span>
                <button
                  type="button"
                  onClick={() => navigate('/register')}
                  className="text-[#10B981] hover:underline font-medium"
                >
                  Registe-se
                </button>
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-[#3B82F6] hover:underline font-medium"
                >
                  Ou faça login com Google
                </button>
              </div>
            </div>
          </form>

          <div className="text-center text-xs text-slate-500">
            © 2026 Privaxia. Todos os direitos reservados.
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginLocalPage;